import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { BrandMark } from "../design/BrandMark";

import { requests, send } from "../api/auth";
import { StatusChip } from "../design/StatusChip";
import { PreSessionLocale, useT } from "../i18n/LocaleProvider";
import { homePathFor } from "../session/access";
import { useSession } from "../session/SessionProvider";
import { translateLabel } from "../i18n/translate";
import { SURFACE_PREFIX } from "../system/navigation";
import { Message } from "./Message";

// ---------------------------------------------------------------------------
// /accept-invitation?token= — the screen an invited person lands on.
//
// SAME TOKEN DISCIPLINE AS /reset, /enter AND /verify: the value is read once
// into memory and the history entry is replaced before anything renders, so
// nothing survives in history or in a later Referer.
//
// WHY THIS SCREEN ASKS FOR A PASSWORD AND SOMETIMES DOES NOT USE IT.
//
// The invitee may already have a Verbunk account — a scheduler at one agency
// invited by another, which is the case the whole platform-scoped identity
// model exists for. The server accepts a password ONLY where the account has
// none, and silently ignores it otherwise, because an invitation that could
// overwrite an existing password would be a password reset that anybody able to
// type an address could cause to be sent.
//
// The screen cannot know which case it is in without asking the server whether
// an address has an account, which is exactly the question the whole of V3 and
// the signup screen are careful not to answer. So it offers the field, labels
// it honestly, and lets the server decide.
// ---------------------------------------------------------------------------

/** S75: a worker accepting an invitation is not known yet, so the browser's language. */
export function AcceptInvitation() {
  return (
    <PreSessionLocale>
      <AcceptInvitationScreen />
    </PreSessionLocale>
  );
}

function AcceptInvitationScreen() {
  const { t, refusal, locale } = useT();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { apiBase, reload } = useSession();

  const captured = useRef<string | null>(null);
  if (captured.current === null) captured.current = params.get("token");
  const token = captured.current;

  useEffect(() => {
    if (params.get("token") !== null) {
      navigate(location.pathname, { replace: true });
    }
  }, [params, navigate]);

  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<{ tone: "failed" | "attention"; status: string; message: string } | null>(
    null,
  );

  // Tour T-6 (0318). Who invited you, and to what. Nothing is shown if the server cannot
  // name it: accepting gives the reason, so a second refusal here would only repeat it.
  const [invitedBy, setInvitedBy] = useState<{ organisation_name: string; role_label: string } | null>(null);
  useEffect(() => {
    if (!token) return;
    let live = true;
    send<{ organisation_name: string; role_label: string }>(requests.previewInvitation(apiBase, token))
      .then(({ status, body }) => {
        if (live && status === 200) setInvitedBy(body);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [token, apiBase]);

  if (!token) {
    return (
      <Message
        tone="failed"
        status={t("Link not valid")}
        title={t("That invitation link is incomplete")}
        action={{ label: t("Go to sign in"), to: "/sign-in" }}
      >
        <p>{t("Open the link from your invitation email again, or ask for a new one.")}</p>
      </Message>
    );
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busy || !token) return;
    setBusy(true);
    setOutcome(null);
    try {
      const { status, body } = await send<{ error?: { message?: string } }>(
        // An empty box means "I already have a password" — sending "" would be
        // a password the policy would then refuse, which reads as the
        // invitation being broken.
        requests.acceptInvitation(apiBase, token, password === "" ? undefined : password),
      );
      if (status !== 200) {
        setOutcome({
          tone: "failed",
          status: t("Not accepted"),
          message: refusal(body.error) ?? t("That invitation is not valid or has expired."),
        });
        return;
      }
      // The session is new — accepting rotates it — so everything derived from
      // the old one is stale. Re-ask rather than merging the response.
      const me = await reload();
      navigate((me && homePathFor(me)) ?? SURFACE_PREFIX.console, { replace: true });
    } catch {
      setOutcome({
        tone: "attention",
        status: t("No connection"),
        message: t("We could not reach Verbunk. Check your connection and try again."),
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="centre-page">
      <BrandMark />
      <div className="centre-card">
        <h1>{t("Accept your invitation")}</h1>
        {invitedBy ? (
          <p>
            {t("{organisation} invited you to join as {role}.", {
              organisation: invitedBy.organisation_name,
              role: translateLabel(locale, invitedBy.role_label),
            })}
          </p>
        ) : null}

        <div className="form-message" role="status" aria-live="polite">
          {outcome ? (
            <>
              <StatusChip tone={outcome.tone} label={outcome.status} />
              <span>{outcome.message}</span>
            </>
          ) : null}
        </div>

        <form onSubmit={submit}>
          <div className="field">
            <label htmlFor="invite-password">{t("Choose a password")}</label>
            <input
              id="invite-password"
              name="password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <p className="field-hint">
              {t("At least twelve characters. Leave this empty if you already sign in to Verbunk — your existing password still works.")}
            </p>
          </div>

          <button className="btn btn-block" type="submit" disabled={busy}>
            {busy ? t("Working…") : t("Accept and continue")}
          </button>
        </form>
      </div>
    </main>
  );
}
